import { useId } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { APP_INVITE, AppInviteConfig } from "./appInviteConfig";

type Props = { invite?: AppInviteConfig; mobile: boolean };

const storeButton = {
  textTransform: "none",
  fontWeight: 600,
  justifyContent: "flex-start",
} as const;

// Só para leitor de tela: os links das lojas abrem em nova aba.
const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;
const NewTab = () => (
  <Box component="span" sx={srOnly}>
    {" (abre em nova aba)"}
  </Box>
);

// Tela pós-envio (D24/D32): incentiva o próximo pedido pelo app, sem oferecer o automático.
export default function AppInvite({ invite = APP_INVITE, mobile }: Props) {
  const titleId = useId();
  const { iosUrl, androidUrl, qrSrc } = invite;
  if (!iosUrl && !androidUrl) return null;
  const showQr = !mobile && Boolean(qrSrc);
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={titleId}
      sx={{
        mt: 3,
        mx: "auto",
        maxWidth: 520,
        p: mobile ? 2 : 2.5,
        borderRadius: 2,
        textAlign: "left",
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <PhoneIphoneIcon sx={{ color: "primary.main" }} aria-hidden />
          <Typography
            component="h3"
            id={titleId}
            sx={{
              fontSize: 16.5,
              fontWeight: 700,
              color: "primary.main",
              lineHeight: 1.35,
            }}
          >
            Da próxima vez, peça pelo app Minha Escala
          </Typography>
        </Box>
        <Typography
          sx={{
            mt: 1,
            fontSize: 14,
            color: "text.secondary",
            lineHeight: 1.55,
          }}
        >
          No app você acompanha cada etapa do pedido e recebe aviso quando o pagamento sair.
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 1,
            flexDirection: mobile ? "column" : "row",
            flexWrap: "wrap",
          }}
        >
          {iosUrl && (
            <Button
              component="a"
              href={iosUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              startIcon={<AppleIcon />}
              sx={storeButton}
              fullWidth={mobile}
            >
              Baixar na App Store
              <NewTab />
            </Button>
          )}
          {androidUrl && (
            <Button
              component="a"
              href={androidUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              startIcon={<AndroidIcon />}
              sx={storeButton}
              fullWidth={mobile}
            >
              Baixar no Google Play
              <NewTab />
            </Button>
          )}
        </Box>
      </Box>
      {showQr && (
        <Box sx={{ flex: "0 0 auto", textAlign: "center", width: 120 }}>
          <Box component="img" src={qrSrc as string} alt="QR code para baixar o app Minha Escala" width={120} height={120} sx={{ display: "block" }} />
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 11.5,
              color: "text.secondary",
              lineHeight: 1.3,
            }}
          >
            Aponte a câmera do celular para baixar
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
